import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, Send, Bot, User } from 'lucide-react'

interface Message {
  id: string
  text: string
  isBot: boolean
  timestamp: Date
}

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I\'m here to help you with your video production and event management needs. How can I assist you today?',
      isBot: true,
      timestamp: new Date(),
    },
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const botResponses = {
    greeting: [
      'Hello! How can I help you today?',
      'Hi there! What can I do for you?',
      'Welcome! I\'m here to assist you.',
    ],
    services: [
      'We offer video production, event management, corporate events, photography, live streaming, and wedding coverage. Which service interests you?',
      'Our main services include:\n• Event Management\n• Video Production\n• Corporate Events\n• Photography\n• Live Streaming\n• Wedding Coverage\n\nWould you like to know more about any specific service?',
    ],
    pricing: [
      'We offer customized packages based on the type and scale of your event. I\'d recommend booking a consultation so we can discuss your specific needs. Would you like me to help you schedule one?',
      'Our services are tailored to your specific requirements like event duration, location, and other needs. Let\'s discuss your event - what type of event are you planning?',
    ],
    booking: [
      'I\'d be happy to help you book an event! You can either fill out our booking form on the website or call us directly. What type of event are you planning?',
      'Great! To get started with booking, I\'ll need some details about your event. What\'s the occasion and when are you planning it?',
    ],
    contact: [
      'You can reach us at:\n📞 +91 787446 7874\n📧 bhavesh7874@gmail.com\n📍 Ahmedabad, Gujarat, India\n🌐 parthvideo.in\n\nOur business hours are Mon-Sat: 9AM-8PM, Sun: 10AM-6PM',
'Feel free to contact us anytime! Phone: +91 787446 7874 or Email: bhavesh7874@gmail.com. We\'re also available for consultations.',
    ],
    default: [
      'I\'m not sure about that, but I\'d be happy to connect you with our team for more detailed information. Would you like me to help you get in touch?',
      'That\'s a great question! For specific details, I recommend speaking with our experts. Can I help you schedule a consultation?',
      'I\'d love to help you with that! For the most accurate information, please contact our team directly or book a consultation.',
    ],
  }

  const getRandomResponse = (category: keyof typeof botResponses) => {
    const responses = botResponses[category]
    return responses[Math.floor(Math.random() * responses.length)]
  }

  const generateBotResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase()
    
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return getRandomResponse('greeting')
    }
    
    if (message.includes('service') || message.includes('what do you do') || message.includes('offer')) {
      return getRandomResponse('services')
    }
    
    if (message.includes('price') || message.includes('cost') || message.includes('rate') || message.includes('quote') || message.includes('pricing')) {
      return getRandomResponse('pricing')
    }
    
    if (message.includes('book') || message.includes('schedule') || message.includes('appointment')) {
      return getRandomResponse('booking')
    }
    
    if (message.includes('contact') || message.includes('phone') || message.includes('email') || message.includes('address')) {
      return getRandomResponse('contact')
    }
    
    return getRandomResponse('default')
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isBot: false,
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsTyping(true)

    // Simulate bot typing delay
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: generateBotResponse(inputValue),
        isBot: true,
        timestamp: new Date(),
      }
      
      setMessages(prev => [...prev, botResponse])
      setIsTyping(false)
    }, 1000 + Math.random() * 1000)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 p-4 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 shadow-lg hover:shadow-xl transition-all duration-300 ${isOpen ? 'hidden' : 'block'}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2 }}
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent-500 rounded-full animate-pulse" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 h-96 glass-card flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/20">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Parth Video Assistant</h3>
                  <p className="text-xs text-white/60">Online now</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-full hover:bg-white/20 transition-colors duration-300"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`flex items-start space-x-2 max-w-[80%] ${
                    message.isBot ? 'flex-row' : 'flex-row-reverse space-x-reverse'
                  }`}>
                    <div className={`p-2 rounded-full flex-shrink-0 ${
                      message.isBot 
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500' 
                        : 'bg-white/20'
                    }`}>
                      {message.isBot ? (
                        <Bot className="w-4 h-4 text-white" />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={`p-3 rounded-2xl ${
                      message.isBot 
                        ? 'bg-white/10 text-white' 
                        : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                    }`}>
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                      <p className="text-xs opacity-60 mt-1">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="flex items-start space-x-2">
                    <div className="p-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="p-3 rounded-2xl bg-white/10">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/20">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 glass-input text-sm"
                  disabled={isTyping}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isTyping}
                  className="p-3 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default ChatBot